import { useNavigate, useSearchParams } from "react-router-dom";
import { XCircle, ArrowLeft, Home, RefreshCcw } from "lucide-react";

export default function PaymentCancel() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bookingId = searchParams.get("booking_id");

  const handleRetryPayment = () => {
    // Navigate back to the previous page (workspace page)
    window.history.back();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-yellow-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-8 text-center">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-12 h-12 text-orange-600" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Payment Cancelled</h1>
          <p className="text-orange-100">Your booking was not completed</p>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Message */}
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-3">
              Your payment was cancelled
            </h2>
            <p className="text-gray-600 leading-relaxed">
              No charges have been made to your account. Your booking has not been confirmed.
              You can try again or return to browse other workspaces.
            </p>
          </div>

          {/* Information Cards */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {/* What Happened Card */}
            <div className="bg-orange-50 rounded-xl p-6">
              <h3 className="font-semibold text-orange-800 mb-3 flex items-center">
                <XCircle className="w-5 h-5 mr-2" />
                What Happened?
              </h3>
              <ul className="text-sm text-orange-700 space-y-2">
                <li>• Payment process was cancelled</li>
                <li>• No charges were made</li>
                <li>• Booking was not created</li>
                <li>• You can try again anytime</li>
              </ul>
            </div>

            {/* Next Steps Card */}
            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
                <ArrowLeft className="w-5 h-5 mr-2" />
                What Can You Do?
              </h3>
              <ul className="text-sm text-blue-700 space-y-2">
                <li>• Try the payment again</li>
                <li>• Use a different payment method</li>
                <li>• Contact support for assistance</li>
                <li>• Browse other workspaces</li>
              </ul>
            </div>
          </div>

          {/* Booking Reference (if available) */}
          {bookingId && (
            <div className="bg-gray-50 rounded-xl p-4 mb-8">
              <p className="text-xs text-gray-500 mb-1">Booking Reference:</p>
              <p className="text-sm font-mono text-gray-700">#{bookingId}</p>
              <p className="text-xs text-gray-500 mt-2">
                This booking was cancelled and is no longer active.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="grid md:grid-cols-3 gap-4">
            <button
              onClick={handleRetryPayment}
              className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-md hover:shadow-lg flex items-center justify-center"
            >
              <RefreshCcw className="w-5 h-5 mr-2" />
              Try Again
            </button>
            <button
              onClick={() => navigate("/")}
              className="bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center"
            >
              <Home className="w-5 h-5 mr-2" />
              Browse Workspaces
            </button>
            <button
              onClick={() => navigate("/account")}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center"
            >
              My Account
            </button>
          </div>

          {/* Help Section */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600 mb-2">Need help?</p>
            <a
              href="mailto:support@airarrange.com"
              className="text-blue-600 hover:text-blue-700 font-medium text-sm underline"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
